import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useReducedMotion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Clock, Server as ServerIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLang } from '@/context/LanguageContext';
import { useOffer } from '@/context/OfferContext';
import ParticleNetwork from '@/components/wow/ParticleNetwork';
import { DashboardMock, ServerMetrics, NetworkMap, TicketConsole } from '@/components/visuals/ITVisuals';

const EASE = [0.16, 1, 0.3, 1];

/* A floating dashboard "window" that assembles into place as the user scrolls. */
function AssembleWindow({ progress, range, from, children, className = '', style = {}, z = 1, reduced }) {
  const [s, e] = range;
  const opacity = useTransform(progress, [s, e], [0, 1]);
  const y = useTransform(progress, [s, e], [from.y ?? 40, 0]);
  const x = useTransform(progress, [s, e], [from.x ?? 0, 0]);
  const scale = useTransform(progress, [s, e], [from.scale ?? 0.94, 1]);
  const oS = useSpring(opacity, { stiffness: 120, damping: 26 });
  const yS = useSpring(y, { stiffness: 120, damping: 26 });
  const xS = useSpring(x, { stiffness: 120, damping: 26 });
  const scaleS = useSpring(scale, { stiffness: 120, damping: 26 });
  if (reduced) {
    return <div className={className} style={{ ...style, zIndex: z }}>{children}</div>;
  }
  return (
    <motion.div className={className} style={{ ...style, zIndex: z, opacity: oS, y: yS, x: xS, scale: scaleS, willChange: 'transform, opacity' }}>
      {children}
    </motion.div>
  );
}

export default function EnterpriseHero() {
  const { t } = useLang();
  const { openOffer } = useOffer();
  const reduced = useReducedMotion();
  const wrapRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start start', 'end start'] });

  // left column subtle parallax + a scan sweep in the assembly phase
  const leftY = useTransform(scrollYProgress, [0, 0.6], [0, -28]);
  const leftYS = useSpring(leftY, { stiffness: 120, damping: 30 });
  const sweepY = useTransform(scrollYProgress, [0.45, 0.85], ['-20%', '120%']);
  const sweepOpacity = useTransform(scrollYProgress, [0.45, 0.6, 0.85], [0, 0.5, 0]);
  const hudOpacity = useTransform(scrollYProgress, [0.3, 0.5], [0, 1]);
  const scrollHintOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0]);

  const trustItems = [
    { icon: ServerIcon, label: t('hero.trust1') },
    { icon: Clock, label: t('hero.trust2') },
    { icon: ShieldCheck, label: t('hero.trust3') },
  ];

  return (
    <section data-testid="home-hero-section" ref={wrapRef} className="relative" style={{ height: reduced ? 'auto' : '190vh' }}>
      <div className={`${reduced ? 'relative' : 'sticky top-0'} h-screen min-h-[680px] gradient-hero overflow-hidden`}>
        <div className="absolute inset-0 itw-grid opacity-40" />
        <ParticleNetwork opacity={0.5} />
        <div className="absolute top-24 right-[-6%] h-[520px] w-[520px] rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, #00E7FF, transparent 70%)' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center pt-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center w-full">
            {/* LEFT — headline */}
            <motion.div className="lg:col-span-6" style={reduced ? {} : { y: leftYS }}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--itw-border-strong)] glass px-4 py-1.5 text-xs font-mono text-[color:var(--itw-cyan-soft)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--itw-cyan)] animate-pulse" />
                {t('hero.badge')}
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                className="mt-6 text-5xl sm:text-6xl lg:text-[68px] font-semibold tracking-tight text-[color:var(--itw-text)] leading-[1.03]">
                {t('hero.title1')}<br /><span className="text-glow bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(120deg, #EAF1FF 0%, #5AF3FF 55%, #00E7FF 100%)' }}>{t('hero.title2')}</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                className="mt-6 text-base sm:text-lg text-[color:var(--itw-text-2)] leading-relaxed max-w-xl">
                {t('hero.subtitle')}
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
                className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button onClick={() => openOffer('')} data-testid="hero-cta-primary"
                  className="h-12 px-6 bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity rounded-full text-base">
                  {t('hero.ctaPrimary')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button onClick={() => document.getElementById('platform')?.scrollIntoView({ behavior: 'smooth' })} data-testid="hero-cta-secondary"
                  className="h-12 px-6 glass border border-[color:var(--itw-border)] text-[color:var(--itw-text)] hover:border-[color:var(--itw-border-strong)] transition-colors rounded-full text-base bg-transparent">
                  {t('hero.ctaSecondary')}
                </Button>
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
                className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
                {trustItems.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-[color:var(--itw-text-2)]">
                    <it.icon className="h-4 w-4 text-[color:var(--itw-cyan)]" /> {it.label}
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* RIGHT — assembling dashboard stack */}
            <div className="lg:col-span-6 relative hidden md:block" style={{ minHeight: 460 }}>
              {/* HUD label + connection lines */}
              <motion.div style={reduced ? {} : { opacity: hudOpacity }} className="absolute -top-6 right-0 z-30 font-mono text-[10px] uppercase tracking-[0.25em] text-[color:var(--itw-cyan-soft)]">
                // systems · online
              </motion.div>

              {/* foreground main dashboard */}
              <AssembleWindow progress={scrollYProgress} range={[0.02, 0.22]} from={{ y: 50, scale: 0.95 }} reduced={reduced}
                className="relative" style={{}} z={20}>
                <div className="neon-edge rounded-xl"><DashboardMock accent="#00E7FF" /></div>
              </AssembleWindow>

              {/* secondary — slides from right */}
              <AssembleWindow progress={scrollYProgress} range={[0.2, 0.42]} from={{ x: 60, y: 20, scale: 0.9 }} reduced={reduced}
                className="absolute -right-6 top-[42%] w-[62%]" z={30}>
                <TicketConsole accent="#5AF3FF" />
              </AssembleWindow>

              {/* tertiary — slides from left/bottom */}
              <AssembleWindow progress={scrollYProgress} range={[0.34, 0.56]} from={{ x: -50, y: 40, scale: 0.9 }} reduced={reduced}
                className="absolute -left-10 -bottom-16 w-[58%]" z={10}>
                <ServerMetrics accent="#00E7FF" />
              </AssembleWindow>

              {/* scan sweep during assembly */}
              {!reduced && (
                <motion.div aria-hidden className="pointer-events-none absolute inset-x-0 z-40 h-40"
                  style={{ y: sweepY, opacity: sweepOpacity, background: 'linear-gradient(180deg, transparent, rgba(0,231,255,0.14) 45%, rgba(90,243,255,0.24) 50%, rgba(0,231,255,0.14) 55%, transparent)' }} />
              )}
            </div>
          </div>
        </div>

        {/* scroll hint */}
        {!reduced && (
          <motion.div style={{ opacity: scrollHintOpacity }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[color:var(--itw-text-3)]">
            <span>scrollen · systeme starten</span>
            <span className="h-6 w-px bg-gradient-to-b from-[color:var(--itw-cyan)] to-transparent animate-pulse" />
          </motion.div>
        )}
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[color:var(--itw-bg)] to-transparent" />
      </div>
    </section>
  );
}
